import dayjs from 'dayjs'
import { EventData } from 'features/systemLens'
import { TimeSeriesService, useScatterplotContext } from 'features/timeSeries'
import { getSensorColor } from 'features/timeSeries/config/timeSeriesColors'
import { useExperimentChartOverlays } from 'features/timeSeries/experiment/ExperimentLabContext'
import { removedPointsDigest } from 'features/timeSeries/experiment/overlays'
import {
  buildSentinelBandOverlays,
  SentinelBandOverlay,
  sentinelOverlayValuesDigest,
  useSentinelScores,
} from 'features/timeSeries/hooks/useSentinelScores'
import {
  isPerfEnabled,
  perfMark,
  perfRenderPrep,
} from 'features/timeSeries/perf/processDataPerf'
import { logAnnotationDebug } from 'features/timeSeries/utils/annotationDebug'
import { estimateSampleIntervalMs } from 'features/timeSeries/utils/sampleCadence'
import {
  clampTimeRange,
  minSpanForSampleIntervalMs,
} from 'features/timeSeries/utils/timeRangeClamp'
import {
  buildUPlotScaleRanges,
  generateUPlotOptions,
} from 'features/timeSeries/utils/uPlotConfig'
import {
  generateUPlotData,
  UPlotSeriesConfig,
} from 'features/timeSeries/utils/uPlotData'
import {
  eventMarkersPlugin,
  experimentMarkersPlugin,
  experimentRemovedPointsPlugin,
  sentinelBandsPlugin,
  thresholdsPlugin,
  tooltipPlugin,
} from 'features/timeSeries/utils/uPlotPlugins'
import { useEffect, useMemo, useRef } from 'react'
import { EnhancedSensorEntry } from 'types'
import uPlot from 'uplot'

interface ThresholdValue {
  value: number
  type: 'lowerLower' | 'lower' | 'upper' | 'upperUpper'
  sensors: string[]
}

export interface UPlotSeriesAppearance {
  alpha?: number
  width?: number
  dash?: number[]
  pointsShow?: boolean
  pointsSize?: number
}

interface UseUPlotGeneratorsProps {
  operationalEvents: EventData[]
  enhancedSensorEntries: EnhancedSensorEntry[]
  uniqueThresholds: ThresholdValue[]
  allSensorsToRender: Array<{ id: string; name: string }>
  width: number
  height: number
  annotationProtectedSensorIds?: string[]
  highlightedSensorIdOverride?: string | null
  onCursorMove?: (idx: number | null, u: uPlot) => void
  onCursorLeave?: () => void
  secondaryHighlightedSensorIdsOverride?: string[]
}

/**
 * The *shape* of a scale's range, with the numbers deliberately left out.
 *
 * Which end is pinned and which is left to uPlot's autoscaling is structural:
 * `[min, null]` and `[min, max]` are configured differently and behave
 * differently. The actual limits are not — moving an axis from [0, 100] to
 * [0, 100.5] is the same chart showing a slightly different window, and is
 * applied to the live instance with `setScale`. Serializing the numbers here is
 * what used to make every range change destroy and rebuild the chart.
 */
function describeScaleRangeShape(
  range: uPlot.Scale['range']
): string[] | string | null {
  if (Array.isArray(range)) {
    return range.map((value) =>
      value === null || value === undefined ? 'auto' : 'fixed'
    )
  }

  return range ? 'dynamic' : null
}

function buildUPlotScaleSignature(scales: uPlot.Scales): string {
  return JSON.stringify(
    Object.entries(scales)
      .sort(([leftKey], [rightKey]) => leftKey.localeCompare(rightKey))
      .map(([scaleKey, scale]) => [
        scaleKey,
        {
          auto: scale.auto ?? null,
          distr: scale.distr ?? null,
          time: scale.time ?? null,
          range: describeScaleRangeShape(scale.range),
        },
      ])
  )
}

function buildUPlotAxisSignature(axes: uPlot.Axis[]): string {
  return JSON.stringify(
    axes.map((axis) => ({
      gap: axis.gap ?? null,
      gridShow: axis.grid?.show ?? null,
      label: axis.label ?? null,
      scale: axis.scale ?? null,
      show: axis.show ?? null,
      side: axis.side ?? null,
      size: axis.size ?? null,
      space: axis.space ?? null,
      stroke: axis.stroke ?? null,
      ticksShow: axis.ticks?.show ?? null,
    }))
  )
}

export function buildUPlotOptionsStructuralSignature(
  options: uPlot.Options
): string {
  const series = (options.series ?? []).map((series) => {
    const config = series as UPlotSeriesConfig

    return {
      isRegression: config._isRegression ?? null,
      label:
        typeof config.label === 'string'
          ? config.label
          : config.label
            ? '[non-string-label]'
            : null,
      scale: config.scale ?? null,
      sensorId: config._sensorId ?? null,
      show: config.show ?? null,
      stroke: config.stroke ?? null,
    }
  })
  const plugins = (options.plugins ?? []).map((plugin) =>
    Object.keys(plugin.hooks ?? {}).sort()
  )

  return JSON.stringify({
    axes: buildUPlotAxisSignature(options.axes ?? []),
    ms: options.ms ?? null,
    plugins,
    scales: buildUPlotScaleSignature(options.scales ?? {}),
    series,
  })
}

export const useUPlotGenerators = ({
  operationalEvents,
  enhancedSensorEntries,
  uniqueThresholds,
  allSensorsToRender,
  width,
  height,
  annotationProtectedSensorIds,
  highlightedSensorIdOverride,
  onCursorMove,
  onCursorLeave,
  secondaryHighlightedSensorIdsOverride,
}: UseUPlotGeneratorsProps) => {
  const {
    siteID,
    timeRange,
    activeSentinels,
    chartData,
    derivedValues,
    regressionConfigs,
    regressionResults,
    unitMapping,
    sensorRanges,
    singleScaleMode,
    connectPoints,
    xAxisSensorId,
    customAxisRanges,
    customXAxisRange,
    sensorAxisMapping,
    dataLayers,
    sensorVisibilityConfig,
    sentinelVisibilityConfig,
    customSensorColors,
    highlightedSensorId,
    secondaryHighlightedSensorIds,
    deemphasizedSensorIds,
    selectedSensors,
    setTimeRange,
    setTimeXAxisPreviewRange,
    exitLiveTail,
    loadTriggerRef,
  } = useScatterplotContext()

  // Pen count for the zoom handler's interval computation, held in a ref for
  // the same reason as minZoomSpanRef below: handleZoom must keep a stable
  // identity, but the interval it bakes into the zoomed range must share the
  // bucket budget across the pens on the chart at zoom time.
  const penCountRef = useRef(selectedSensors.length)
  useEffect(() => {
    penCountRef.current = selectedSensors.length
  }, [selectedSensors.length])

  const effectiveHighlightedSensorId =
    highlightedSensorIdOverride ?? highlightedSensorId
  const effectiveSecondaryHighlightedSensorIds =
    secondaryHighlightedSensorIdsOverride ?? secondaryHighlightedSensorIds

  const uPlotDataResult = useMemo(() => {
    // generateUPlotData (and the resample inside it) is the O(rows x pens)
    // step the fetch records' mergeMs stops short of -- it runs here, after
    // firstDataReady. Measured where it runs; inert unless the perf flag is on.
    const renderPrepStart = isPerfEnabled() ? performance.now() : null
    const result = generateUPlotData({
      dataLayers,
      chartData,
      allSensorsToRender,
      enhancedSensorEntries,
      unitMapping,
      sensorAxisMapping,
      regressionConfigs,
      regressionResults,
      derivedValues,
      xAxisSensorId,
      connectPoints,
      sensorVisibilityConfig,
      customSensorColors,
      highlightedSensorId: effectiveHighlightedSensorId,
      secondaryHighlightedSensorIds: effectiveSecondaryHighlightedSensorIds,
      annotationProtectedSensorIds,
      deemphasizedSensorIds,
      singleScaleMode,
    })
    if (renderPrepStart !== null) {
      perfRenderPrep(performance.now() - renderPrepStart)
      // One-shot milestone to line up against firstDataReady, matching the
      // existing mark pattern; the accumulator above records every rerun.
      perfMark('firstRenderPrepDone')
    }
    return result
  }, [
    dataLayers,
    chartData,
    allSensorsToRender,
    enhancedSensorEntries,
    unitMapping,
    sensorAxisMapping,
    regressionConfigs,
    regressionResults,
    derivedValues,
    xAxisSensorId,
    connectPoints,
    sensorVisibilityConfig,
    customSensorColors,
    effectiveHighlightedSensorId,
    effectiveSecondaryHighlightedSensorIds,
    annotationProtectedSensorIds,
    deemphasizedSensorIds,
    singleScaleMode,
  ])

  // Store cursor callbacks in refs so the tooltip plugin instance (and therefore
  // the plugins array and uPlotOptions) stays referentially stable when only
  // the callback identity changes. This prevents chart destruction/recreation
  // during drag-to-zoom caused by React state updates triggering re-renders.
  const onCursorMoveRef = useRef(onCursorMove)
  const onCursorLeaveRef = useRef(onCursorLeave)
  useEffect(() => {
    onCursorMoveRef.current = onCursorMove
  }, [onCursorMove])
  useEffect(() => {
    onCursorLeaveRef.current = onCursorLeave
  }, [onCursorLeave])

  const thresholdsEnabled =
    dataLayers.find((layer) => layer.id === 'thresholds')?.enabled ?? false
  const eventsEnabled =
    dataLayers.find((layer) => layer.id === 'events')?.enabled ?? false
  const sentinelsEnabled =
    dataLayers.find((layer) => layer.id === 'sentinels')?.enabled ?? false

  const { scores: sentinelScores } = useSentinelScores(
    siteID,
    activeSentinels,
    timeRange,
    { enabled: sentinelsEnabled }
  )
  const sentinelOverlays = useMemo(
    () =>
      sentinelsEnabled
        ? buildSentinelBandOverlays({
            // Individually-hidden sentinels stay active (their drawer row is
            // the way back) but draw nothing.
            activeSentinels: activeSentinels.filter(
              (sentinel) => sentinelVisibilityConfig[sentinel.entryId] !== false
            ),
            scores: sentinelScores,
            allSensorsToRender,
            customSensorColors,
          })
        : [],
    [
      sentinelsEnabled,
      activeSentinels,
      sentinelVisibilityConfig,
      sentinelScores,
      allSensorsToRender,
      customSensorColors,
    ]
  )

  // Experiment Lab overlays (EXPERIMENT sidebar tab): removed-point markers
  // from the data preview and the trained expected/residual band. The band
  // reuses the sentinel band overlay shape/plugin; its color follows the
  // target pen, exactly like sentinel bands.
  const {
    bandOverlay: experimentBandOverlay,
    removedPoints: experimentRemovedPoints,
    outOfBandPoints: experimentOutOfBandPoints,
    testBoundaryMs: experimentTestBoundaryMs,
  } = useExperimentChartOverlays()
  const experimentOverlays = useMemo((): SentinelBandOverlay[] => {
    if (!experimentBandOverlay) return []
    const penIndex = allSensorsToRender.findIndex(
      (sensor) => sensor.id === experimentBandOverlay.targetSensorId
    )
    const color =
      customSensorColors?.[experimentBandOverlay.targetSensorId] ??
      getSensorColor(penIndex >= 0 ? penIndex : 0)
    return [{ ...experimentBandOverlay, color }]
  }, [experimentBandOverlay, allSensorsToRender, customSensorColors])
  // Parallel arrays for the marker plugin (dots at out-of-band actuals).
  const experimentOutOfBand = useMemo(
    () => ({
      xs: experimentOutOfBandPoints.map((point) => point.x),
      values: experimentOutOfBandPoints.map((point) => point.actual),
    }),
    [experimentOutOfBandPoints]
  )

  const pluginStructuralSignature = useMemo(
    () =>
      JSON.stringify({
        events: eventsEnabled
          ? operationalEvents.map((event) => ({
              description: event.description,
              priority: event.metadata?.priority ?? null,
              status: event.metadata?.status ?? null,
              timestamp: event.timestamp,
              type: event.type,
            }))
          : [],
        eventsEnabled,
        sensorAxisMapping,
        // The plugin instance closes over the band series, so the chart must
        // recreate when they change. The raw arrays would bloat the
        // signature, so digest them: window + method + length, plus a values
        // checksum — endpoints and count alone can collide across responses
        // whose interior values differ (same surviving buckets, new scores),
        // which would leave a stale plugin closure drawing the old band.
        sentinelOverlays: sentinelOverlays.map((overlay) => ({
          color: overlay.color,
          firstX: overlay.xs[0] ?? null,
          label: overlay.label,
          lastX: overlay.xs[overlay.xs.length - 1] ?? null,
          method: overlay.method,
          points: overlay.xs.length,
          target: overlay.targetSensorId,
          valuesDigest: sentinelOverlayValuesDigest(overlay),
        })),
        sentinelsEnabled,
        singleScaleMode,
        thresholds: thresholdsEnabled ? uniqueThresholds : [],
        thresholdsEnabled,
        thresholdSensorIds: thresholdsEnabled
          ? enhancedSensorEntries.map((entry) => entry.id)
          : [],
        tooltip: true,
        // Same closure-staleness rule as sentinel overlays: the experiment
        // plugins close over their series, so a changed preview / train
        // result must recreate the chart.
        experimentOverlays: experimentOverlays.map((overlay) => ({
          color: overlay.color,
          method: overlay.method,
          points: overlay.xs.length,
          target: overlay.targetSensorId,
          valuesDigest: sentinelOverlayValuesDigest(overlay),
        })),
        experimentRemovedPoints: experimentRemovedPoints.map((series) => ({
          points: series.xs.length,
          sensorId: series.sensorId,
        })),
        experimentRemovedPointsDigest: removedPointsDigest(
          experimentRemovedPoints
        ),
        // Boundary + dot markers close over their arrays too. The dots
        // derive from the band overlay (already value-digested above), so
        // count + boundary is enough here.
        experimentTestBoundaryMs,
        experimentOutOfBandCount: experimentOutOfBand.xs.length,
      }),
    [
      enhancedSensorEntries,
      eventsEnabled,
      experimentOverlays,
      experimentRemovedPoints,
      experimentOutOfBand,
      experimentTestBoundaryMs,
      operationalEvents,
      sensorAxisMapping,
      sentinelOverlays,
      sentinelsEnabled,
      singleScaleMode,
      thresholdsEnabled,
      uniqueThresholds,
    ]
  )

  const plugins = useMemo(() => {
    const pluginList: uPlot.Plugin[] = []

    if (thresholdsEnabled && uniqueThresholds.length > 0) {
      pluginList.push(
        thresholdsPlugin({
          thresholds: uniqueThresholds,
          sensorAxisMapping,
          enhancedSensorEntries,
          singleScaleMode,
        })
      )
    }

    if (eventsEnabled && operationalEvents.length > 0) {
      pluginList.push(eventMarkersPlugin({ events: operationalEvents }))
    }

    if (sentinelsEnabled && sentinelOverlays.length > 0) {
      pluginList.push(
        sentinelBandsPlugin({
          overlays: sentinelOverlays,
          sensorAxisMapping,
          singleScaleMode,
        })
      )
    }

    // Experiment overlays are independent of the Sentinels data layer: the
    // band reuses the sentinel band plugin (same drawing), the removed
    // points get their own marker plugin. Unlike sentinels' faint center
    // dotting, the experiment's expected line draws prominent — it IS the
    // model's prediction the user trained to see.
    if (experimentOverlays.length > 0) {
      pluginList.push(
        sentinelBandsPlugin({
          overlays: experimentOverlays,
          sensorAxisMapping,
          singleScaleMode,
          centerLine: { alpha: 0.9, widthPx: 1.5, dashPx: [8, 4] },
        })
      )
    }

    if (experimentRemovedPoints.length > 0) {
      pluginList.push(
        experimentRemovedPointsPlugin({
          removedPoints: experimentRemovedPoints,
          sensorAxisMapping,
          singleScaleMode,
        })
      )
    }

    // Test-split boundary line + out-of-band dots, on top of the pens.
    if (
      experimentOverlays.length > 0 &&
      (experimentTestBoundaryMs !== null || experimentOutOfBand.xs.length > 0)
    ) {
      pluginList.push(
        experimentMarkersPlugin({
          targetSensorId: experimentOverlays[0].targetSensorId,
          testBoundaryMs: experimentTestBoundaryMs,
          outOfBandXs: experimentOutOfBand.xs,
          outOfBandValues: experimentOutOfBand.values,
          sensorAxisMapping,
          singleScaleMode,
        })
      )
    }

    pluginList.push(
      tooltipPlugin({
        onCursorMoveRef,
        onCursorLeaveRef,
      })
    )

    return pluginList
  }, [
    enhancedSensorEntries,
    eventsEnabled,
    experimentOverlays,
    experimentRemovedPoints,
    experimentOutOfBand,
    experimentTestBoundaryMs,
    operationalEvents,
    sensorAxisMapping,
    sentinelOverlays,
    sentinelsEnabled,
    singleScaleMode,
    thresholdsEnabled,
    uniqueThresholds,
  ])

  // How far in this chart's data can honestly be zoomed. Held in a ref, not
  // closed over: `handleZoom` must keep a stable identity (see the comment on
  // `onCursorMoveRef`) or the live uPlot instance keeps calling a stale copy.
  const minZoomSpanRef = useRef(minSpanForSampleIntervalMs())
  const chartDataTimestamps = useMemo(
    () =>
      chartData
        .map((point) => (point as { timestamp?: unknown }).timestamp)
        .filter(
          (timestamp): timestamp is number => typeof timestamp === 'number'
        ),
    [chartData]
  )
  useEffect(() => {
    minZoomSpanRef.current = minSpanForSampleIntervalMs(
      estimateSampleIntervalMs(chartDataTimestamps)
    )
  }, [chartDataTimestamps])

  const handleZoom = useMemo(() => {
    if (xAxisSensorId) return undefined

    return (min: number, max: number) => {
      // `min`/`max` are pixel-derived, so they can be inverted, zero-width, or
      // past `now`. Sanitize before committing -- getInterval would happily turn
      // a degenerate range into a valid-looking 1s request returning no points.
      // The floor comes from the cadence of the data actually on screen: a drag
      // narrower than a few samples is a request for a chart that cannot exist.
      const clamped = clampTimeRange(min, max, {
        minSpanMs: minZoomSpanRef.current,
      })
      if (!clamped) return

      const fromISO = dayjs(clamped.from).toISOString()
      const toISO = dayjs(clamped.to).toISOString()

      const timeRangeSeconds = (clamped.to - clamped.from) / 1000
      const { interval, interval_unit } =
        TimeSeriesService.getIntervalForPenCount(
          timeRangeSeconds,
          penCountRef.current
        )

      // Tell the data processor to keep the previous chartData visible while
      // the new range loads — zoom is the one action that benefits from a
      // placeholder view.
      loadTriggerRef.current = 'zoom'
      // Pin the x window to the zoomed range for the duration of the load. The
      // preserved (wider) data would otherwise drag the auto x-scale back to its
      // full extent until the fetch lands — the view rubber-banding to the old
      // window and only snapping to the zoom once data arrives. The commit
      // effect clears this pin once the loaded data matches the zoomed window.
      setTimeXAxisPreviewRange({ min: clamped.from, max: clamped.to })
      // A zoom pins an explicit window, so leave live tail. Without this the
      // committed range survives only until the next live-tail tick, which
      // re-anchors `timeRange` to "now - lookback" and silently reverts the
      // zoom (the right-drag box zoom and the pan arrows already exit).
      exitLiveTail()
      setTimeRange({
        from: fromISO,
        to: toISO,
        interval,
        intervalUnit: interval_unit,
      })
    }
  }, [
    xAxisSensorId,
    setTimeRange,
    setTimeXAxisPreviewRange,
    exitLiveTail,
    loadTriggerRef,
  ])

  // Stabilize the series reference so data-only changes (same sensor structure)
  // use setData() instead of recreating the uPlot instance.
  const prevSeriesRef = useRef<UPlotSeriesConfig[]>(uPlotDataResult.series)
  const pendingSeriesChangeLogRef = useRef<Record<string, unknown> | null>(null)
  const stableSeries = useMemo(() => {
    const prev = prevSeriesRef.current
    const next = uPlotDataResult.series

    if (prev.length !== next.length) {
      pendingSeriesChangeLogRef.current = {
        reason: 'series-count-changed',
        previousSeriesCount: prev.length,
        nextSeriesCount: next.length,
        previousSensorIds: prev.map((series) => series._sensorId ?? null),
        nextSensorIds: next.map((series) => series._sensorId ?? null),
      }
      prevSeriesRef.current = next
      return next
    }

    const structureChanges = next.flatMap((s, i) => {
      const p = prev[i]
      const changedFields: string[] = []

      if (s.label !== p.label) changedFields.push('label')
      if (s.scale !== p.scale) changedFields.push('scale')
      if (s.show !== p.show) changedFields.push('show')
      if (s.stroke !== p.stroke) changedFields.push('stroke')
      if (s._sensorId !== p._sensorId) changedFields.push('sensorId')
      if (s._isRegression !== p._isRegression) {
        changedFields.push('isRegression')
      }

      if (changedFields.length === 0) return []

      return [
        {
          seriesIndex: i,
          sensorId: s._sensorId ?? null,
          changedFields,
        },
      ]
    })

    if (structureChanges.length > 0) {
      pendingSeriesChangeLogRef.current = {
        reason: 'series-structure-changed',
        changeCount: structureChanges.length,
        changes: structureChanges.slice(0, 6),
        truncated: structureChanges.length > 6,
        highlightOnly: structureChanges.every(({ changedFields }) =>
          changedFields.every((field) => field === 'alpha' || field === 'width')
        ),
        highlightedSensorId: effectiveHighlightedSensorId,
        secondaryHighlightedSensorIds: effectiveSecondaryHighlightedSensorIds,
        annotationProtectedSensorIds,
      }
      prevSeriesRef.current = next
      return next
    }

    pendingSeriesChangeLogRef.current = null
    return prev
  }, [
    annotationProtectedSensorIds,
    effectiveHighlightedSensorId,
    effectiveSecondaryHighlightedSensorIds,
    uPlotDataResult.series,
  ])

  useEffect(() => {
    if (!pendingSeriesChangeLogRef.current) return

    logAnnotationDebug(
      'uplot-series-structure-change',
      pendingSeriesChangeLogRef.current
    )
  }, [stableSeries])

  const stableOptionsRef = useRef<uPlot.Options | null>(null)
  const stableOptionsSignatureRef = useRef<string | null>(null)
  const stablePluginSignatureRef = useRef<string | null>(null)
  // Everything the scatter/line mode toggle, pen highlight and de-emphasis
  // change about a series lives here, NOT in the stableSeries structural diff:
  // these fields are re-asserted onto the live instance (see useUPlotChart)
  // precisely so that changing them never destroys and recreates the chart.
  const uPlotSeriesAppearance = useMemo(
    () =>
      uPlotDataResult.series.map((series) => ({
        alpha: series.alpha,
        width: series.width,
        dash: series.dash,
        pointsShow: series.points?.show,
        pointsSize: series.points?.size,
      })),
    [uPlotDataResult.series]
  )
  const { uPlotOptions, uPlotRecreateKey } = useMemo(() => {
    const opts = generateUPlotOptions({
      sensorAxisMapping,
      singleScaleMode,
      allSensorsToRender,
      enhancedSensorEntries,
      customAxisRanges,
      sensorRanges,
      derivedValues,
      xAxisSensorId: xAxisSensorId || undefined,
      customXAxisRange,
      unitMapping,
      customSensorColors,
      width,
      height,
      onZoom: handleZoom,
      plugins,
    })

    opts.series = stableSeries

    // Two different questions, deliberately keyed differently.
    //
    // `uPlotRecreateKey` answers "is this a different chart?" and must stay
    // structural: series, axes, plugins. Range *numbers* are excluded, because
    // a new window is the same chart showing different limits — it is applied
    // to the live instance with `setScale`, not by rebuilding it.
    //
    // Object retention answers the narrower "is this literally the same
    // config?", and does include the numbers: the retained object is the one
    // `useUPlotChart` reads limits from, so retaining it across a range change
    // would hand it stale limits to apply.
    const nextSignature = buildUPlotOptionsStructuralSignature(opts)
    const nextRecreateKey = `${nextSignature}|${pluginStructuralSignature}`
    const nextRetentionSignature = `${nextSignature}|${JSON.stringify(
      buildUPlotScaleRanges(opts.scales ?? {})
    )}`
    const previousOptions = stableOptionsRef.current

    if (
      previousOptions &&
      stableOptionsSignatureRef.current === nextRetentionSignature &&
      stablePluginSignatureRef.current === pluginStructuralSignature
    ) {
      return {
        uPlotOptions: previousOptions,
        uPlotRecreateKey: nextRecreateKey,
      }
    }

    stableOptionsRef.current = opts
    stableOptionsSignatureRef.current = nextRetentionSignature
    stablePluginSignatureRef.current = pluginStructuralSignature

    return {
      uPlotOptions: opts,
      uPlotRecreateKey: nextRecreateKey,
    }
  }, [
    sensorAxisMapping,
    singleScaleMode,
    allSensorsToRender,
    enhancedSensorEntries,
    customAxisRanges,
    sensorRanges,
    derivedValues,
    xAxisSensorId,
    customXAxisRange,
    unitMapping,
    customSensorColors,
    width,
    height,
    handleZoom,
    pluginStructuralSignature,
    plugins,
    stableSeries,
  ])

  const hasDataPoints = useMemo(() => {
    const data = uPlotDataResult.data
    if (!Array.isArray(data) || data.length < 2) return false
    const xValues = data[0]
    return Array.isArray(xValues) && xValues.length > 0
  }, [uPlotDataResult.data])

  return {
    uPlotOptions,
    uPlotRecreateKey,
    uPlotSeriesAppearance,
    uPlotData: uPlotDataResult.data,
    hasDataPoints,
  }
}
